import { css, jsx } from '@emotion/core';
import { useState, Fragment, useMemo, useCallback, useEffect } from 'react';
import { FormLabel, Text, Radio, Colors, Modal, ModalContent, Margin, Heading, ButtonGroup, Button, ServiceIcon, Spacer, Typography, Icon } from '@zapier/design-system';
import { emit } from '@zapier/events-toolkit-browser';
import { v4 } from 'uuid';

let RadioButtonValues;

(function (RadioButtonValues) {
  RadioButtonValues["yes"] = "yes";
  RadioButtonValues["no"] = "no";
})(RadioButtonValues || (RadioButtonValues = {}));

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject$3;
const styles$2 = {
  radioButtonsWrapper: css(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n    display: flex;\n    gap: 20px;\n\n    label {\n      display: inline-flex !important;\n      gap: 5px !important;\n      align-items: center !important;\n    }\n  "])))
};
var styles$3 = styles$2;

/** @jsxRuntime classic */
/**
 * Renders a radio button field to allow the user to
 * start a transfer with the zap created
 *
 * @param TransferDataOptionProps
 * @returns TransferDataOptionComponent
 */

const TransferDataOption = _ref => {
  let {
    onChangeTransfer
  } = _ref;
  const [transferData, setTransferData] = useState(RadioButtonValues.yes);

  const onChangeTransferWrapper = e => {
    setTransferData(e.target.value);
    onChangeTransfer(transferData);
  };

  return jsx(Fragment, null, jsx(FormLabel, null, "Do you have existing data to transfer?"), jsx(Text, {
    color: "neutral700",
    margin: "8px 0px 16px 0px",
    tag: "p",
    type: "smallPrint1"
  }, "This will trigger a Transfer post-Zap."), jsx("section", {
    css: styles$3.radioButtonsWrapper
  }, jsx(FormLabel, {
    isSelected: transferData === RadioButtonValues.yes
  }, jsx(Radio, {
    checked: transferData === RadioButtonValues.yes,
    name: "radio",
    onChange: onChangeTransferWrapper,
    value: RadioButtonValues.yes
  }), "Yes"), jsx(FormLabel, {
    isSelected: transferData === RadioButtonValues.no
  }, jsx(Radio, {
    checked: transferData === RadioButtonValues.no,
    name: "radio",
    onChange: onChangeTransferWrapper,
    value: RadioButtonValues.no
  }), "No")));
};

var TransferDataOption$1 = TransferDataOption;

/** @jsxRuntime classic */

const Toggle = _ref => {
  let {
    color = 'neutral700'
  } = _ref;
  return jsx("svg", {
    fill: "none",
    height: "34",
    viewBox: "0 -1 62 36",
    width: "59",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("path", {
    d: "M0 17C0 7.61116 7.61116 0 17 0H45C54.3888 0 62 7.61116 62 17V17C62 26.3888 54.3888 34 45 34H17C7.61116 34 0 26.3888 0 17V17Z",
    fill: "#FFFDF9"
  }), jsx("circle", {
    cx: "44.1748",
    cy: "17",
    fill: "#0F884E",
    r: "11.125",
    stroke: "#d7d5d2"
  }), jsx("path", {
    d: "M17 1H45V-1H17V1ZM45 33H17V35H45V33ZM17 33C8.16344 33 1 25.8366 1 17H-1C-1 26.9411 7.05887 35 17 35V33ZM61 17C61 25.8366 53.8366 33 45 33V35C54.9411 35 63 26.9411 63 17H61ZM45 1C53.8366 1 61 8.16344 61 17H63C63 7.05887 54.9411 -1 45 -1V1ZM17 -1C7.05888 -1 -1 7.05887 -1 17H1C1 8.16344 8.16344 1 17 1V-1Z",
    fill: Colors[color]
  }));
};

var Toggle$1 = Toggle;

const ordinal = i => {
  if (!i) {
    return '';
  }

  const j = i % 10;
  const k = i % 100;

  if (j === 1 && k !== 11) {
    return 'st';
  }

  if (j === 2 && k !== 12) {
    return 'nd';
  }

  if (j === 3 && k !== 13) {
    return 'rd';
  }

  return 'th';
};

const describeSchedule = (frequency, _ref) => {
  let {
    hod,
    dow,
    dom
  } = _ref;

  switch (frequency) {
    case 'every_hour':
      return 'Runs every hour';

    case 'every_day':
      return "Runs every day at ".concat(hod);

    case 'every_week':
      return "Runs every week on ".concat(dow, " at ").concat(hod);

    case 'every_month':
      return "Runs every month on the ".concat(dom).concat(ordinal(dom), " at ").concat(hod);

    default:
      return '';
  }
};

const rangeFromOneTo = (x, transform) => Array(x).fill(0).map((_, i) => transform(i + 1));

const HOURS_OF_DAY = ['midnight', ...rangeFromOneTo(11, hour => "".concat(hour, "am")), 'noon', ...rangeFromOneTo(11, hour => "".concat(hour, "pm"))];
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_OF_MONTH = rangeFromOneTo(31, n => "".concat(n));

const choiceToIndex = (choices, offset, value) => {
  if (typeof value === 'undefined') {
    return undefined;
  }

  const index = choices.indexOf(value);
  return index === -1 ? undefined : "".concat(index + offset);
};

const indexToChoice = (choices, offset, index) => index ? choices[parseInt(index) - offset] : undefined;

const hodToIndex = choiceToIndex.bind(null, HOURS_OF_DAY, 0);
const dowToIndex = choiceToIndex.bind(null, DAYS_OF_WEEK, 0);
const domToIndex = choiceToIndex.bind(null, DAYS_OF_MONTH, 1);
const indexToHod = indexToChoice.bind(null, HOURS_OF_DAY, 0);
const indexToDow = indexToChoice.bind(null, DAYS_OF_WEEK, 0);
const indexToDom = indexToChoice.bind(null, DAYS_OF_MONTH, 1);

// TODO: unit tests
const groupFilters = filters => filters.reduce((groups, criteria) => {
  const index = groups.findIndex(g => g.group === criteria.group);
  const group = groups[index];

  if (!group) {
    return [...groups, {
      group: criteria.group,
      criterion: [criteria]
    }];
  }

  return [...groups.slice(0, index), { ...group,
    criterion: [...group.criterion, criteria]
  }, ...groups.slice(index + 1)];
}, []);

const CONDITIONS = [{
  label: '(Text) Contains',
  value: 'icontains|continue',
  description: 'contains',
  hasValue: true
}, {
  label: '(Text) Does not contain',
  value: 'icontains|stop',
  description: 'does not contain',
  hasValue: true
}, {
  label: '(Text) Exactly matches',
  value: 'iexact|continue',
  description: 'exactly matches',
  hasValue: true
}, {
  label: '(Text) Does not exactly match',
  value: 'iexact|stop',
  description: 'does not exactly match',
  hasValue: true
}, {
  label: '(Text) Is in',
  value: 'ireversecontains|continue',
  description: 'is in',
  hasValue: true
}, {
  label: '(Text) Is not in',
  value: 'ireversecontains|stop',
  description: 'is not in',
  hasValue: true
}, {
  label: '(Text) Starts with',
  value: 'istartswith|continue',
  description: 'starts with',
  hasValue: true
}, {
  label: '(Text) Does not start with',
  value: 'istartswith|stop',
  description: 'does not start with',
  hasValue: true
}, {
  label: '(Text) Ends with',
  value: 'iendswith|continue',
  description: 'ends with',
  hasValue: true
}, {
  label: '(Text) Does not end with',
  value: 'iendswith|stop',
  description: 'does not end with',
  hasValue: true
}, {
  label: '(Number) Greater than',
  value: 'igreater|continue',
  description: 'is greater than',
  hasValue: true
}, {
  label: '(Number) Less than',
  value: 'iless|continue',
  description: 'is less than',
  hasValue: true
}, {
  label: '(Date/time) After',
  value: 'idategreater|continue',
  description: 'is after',
  hasValue: true
}, {
  label: '(Date/time) Before',
  value: 'idateless|continue',
  description: 'is before',
  hasValue: true
}, {
  label: '(Date/time) Equals',
  value: 'idateequal|continue',
  description: 'equals',
  hasValue: true
}, {
  label: '(Boolean) Is true',
  value: 'boolean|continue',
  description: 'is true',
  hasValue: false
}, {
  label: '(Boolean) Is false',
  value: 'boolean|stop',
  description: 'is false',
  hasValue: false
}, {
  label: 'Exists',
  value: 'iexist|continue',
  description: 'exists',
  hasValue: false
}, {
  label: 'Does not exist',
  value: 'iexist|stop',
  description: 'does not exist',
  hasValue: false
}];
const CONDITIONS_BY_VALUE = CONDITIONS.reduce((dict, cond) => {
  dict[cond.value] = cond;
  return dict;
}, {});

// Avoid applying a `max-width` so that we can allow a cascading effect to
// happen, e.g. tablet styles will apply if desktop styles are not provided.
const mediaQueries = {
  mobile: "@media (min-width: 0)",
  tablet: "@media (min-width: 660px)",
  desktop: "@media (min-width: 1024px)"
};
const getResponsiveStyles = styles => {
  return [{
    [mediaQueries.mobile]: css(styles.mobile) || {}
  }, {
    [mediaQueries.tablet]: css(styles.tablet) || {}
  }, {
    [mediaQueries.desktop]: css(styles.desktop) || {}
  }];
};
const getResponsiveSize = () => {
  // With nextjs, the code is executed on the server side for _document
  //  since the window doesn't mount on time we need to check if it's defined
  if (typeof window !== 'undefined') {
    var _window, _window2, _window3;

    if ((_window = window) !== null && _window !== void 0 && _window.matchMedia('(min-width: 1024px)').matches) {
      return 'desktop';
    }

    if ((_window2 = window) !== null && _window2 !== void 0 && _window2.matchMedia('(max-width: 660px)').matches) {
      return 'tablet';
    }

    if ((_window3 = window) !== null && _window3 !== void 0 && _window3.matchMedia('(min-width: 0)').matches) {
      return 'mobile';
    }
  }

  return 'desktop';
};

var _templateObject$2, _templateObject2$2, _templateObject3$1, _templateObject4$1, _templateObject5$1, _templateObject6$1, _templateObject7$1, _templateObject8$1, _templateObject9$1, _templateObject10, _templateObject11;
const styles = {
  containerStyles: css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n    border-radius: inherit;\n    text-align: center;\n  "]))),
  contentStyles: getResponsiveStyles({
    desktop: css(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n      padding: 30px;\n    "]))),
    tablet: css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n      padding: 30px 0px;\n    "]))),
    mobile: css(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n      padding: 30px 0px;\n    "])))
  }),
  textWrapper: css(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n    word-break: break-word;\n    white-space: break-spaces;\n    margin: 40px 0px;\n  "]))),
  apps: css(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  "]))),
  buttonWrapper: css(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteral(["\n    max-width: 200px;\n    margin: 0 auto;\n  "]))),
  iconBadgeContainer: getResponsiveStyles({
    desktop: css(_templateObject8$1 || (_templateObject8$1 = _taggedTemplateLiteral(["\n      position: relative;\n\n      > svg {\n        position: absolute;\n        display: block;\n        top: -20px;\n        right: -20px;\n        width: 45px;\n      }\n    "]))),
    tablet: css(_templateObject9$1 || (_templateObject9$1 = _taggedTemplateLiteral(["\n      position: relative;\n\n      > svg {\n        position: absolute;\n        display: block;\n        top: -18px;\n        right: -15px;\n        width: 30px;\n      }\n    "]))),
    mobile: css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n      position: relative;\n\n      > svg {\n        position: absolute;\n        display: block;\n        top: -16px;\n        right: -10px;\n        width: 24px;\n      }\n    "])))
  }),
  lineConnector: css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    content: ' ';\n    width: 20px;\n    border: 1px solid ", ";\n  "])), Colors.neutral400)
};
var styles$1 = styles;

const USER_INTERACTION_EVENT_SCHEMA = 'web_analytics.tracking.user_interaction.UserInteractionEvent';
const PAGE_VIEW_EVENT_SCHEMA = 'web_analytics.tracking.page_view.PageViewEvent';
const useTracking = transferDataUrl => {
  const commonUserInteractionData = useMemo(() => {
    const [, zapId] = transferDataUrl.match(/\/records\/(\d+)/i) || [];
    const node_zap_id = zapId && !isNaN(Number(zapId)) ? Number(zapId) : null;
    return {
      event_action: 'click',
      node_zap_id,
      popup_flag: true,
      interaction_name: 'confirm button',
      interaction_raw_text: 'prompt: send to transfer',
      interaction_goal: 'send to transfer',
      system_name: 'transfer'
    };
  }, [transferDataUrl]);
  const emitUserInteractionEvent = useCallback(option_selected => {
    emit({ ...commonUserInteractionData,
      event_id: v4(),
      option_selected
    }, USER_INTERACTION_EVENT_SCHEMA);
  }, [commonUserInteractionData]);
  const handlers = useMemo(() => ({
    trackGoToMyZaps() {
      emitUserInteractionEvent('go to my zaps');
    },

    trackTransferExistingData() {
      emitUserInteractionEvent('transfer existing data');
    },

    trackCloseModal() {
      emitUserInteractionEvent('close modal');
    },

    trackOpenModal() {
      emit({
        modal_flag: true,
        modal_name: 'prompt: send to transfer',
        modal_view_name: 'prompt: send to transfer',
        system_name: 'transfer'
      }, PAGE_VIEW_EVENT_SCHEMA);
    }

  }), [emitUserInteractionEvent]);
  return handlers;
};

const vowels = {
  a: true,
  e: true,
  i: true,
  o: true,
  u: true
};
/**
 * Prepends either "a " or "an " to a string.
 *
 * (Copied from [@zapier/common-utils](https://gitlab.com/zapier/zapier/-/blob/d435c04c83d2b9879c313aef754dc2714016636e/packages/libraries/common-utils/src/prefixWithArticle.ts))
 */

const prefixWithArticle = word => {
  if (vowels[word[0].toLowerCase()]) {
    return 'an ' + word;
  } else {
    return 'a ' + word;
  }
};

var prefixWithArticle$1 = prefixWithArticle;

/** @jsxRuntime classic */
/**
 * After a zap is created, renders a modalbox
 * that allows the user to either go back to the zaps
 * or initiate a transfer with the existing data
 *
 * @param PostSetupModalProps
 * @returns PostSetupModalComponent
 */

const PostSetupModal = _ref => {
  let {
    goToMyZapsUrl,
    transferDataUrl,
    readAppLogo,
    readAppName,
    writeAppLogo,
    readActionNoun,
    onClosed,
    useTracking: useTracking$1 = useTracking
  } = _ref;
  const {
    trackGoToMyZaps,
    trackOpenModal,
    trackTransferExistingData
  } = useTracking$1(transferDataUrl);
  useEffect(trackOpenModal, [trackOpenModal]);
  const verb = readActionNoun ? "create ".concat(prefixWithArticle$1(readActionNoun)) : 'trigger the action';
  const description = "Transfer existing data, then ".concat(verb, " in ").concat(readAppName, " to try it out.");
  return jsx(Modal, {
    ariaLabel: "Transfer data modal",
    onClosed: onClosed
  }, jsx(ModalContent, null, jsx("div", {
    css: styles$1.containerStyles
  }, jsx("section", {
    css: styles$1.contentStyles
  }, jsx("div", {
    css: styles$1.apps,
    role: "presentation"
  }, readAppLogo, jsx("div", {
    css: styles$1.lineConnector
  }), jsx(Toggle$1, null), jsx("div", {
    css: styles$1.lineConnector
  }), writeAppLogo), jsx(Margin, {
    margin: "40px 0px"
  }, jsx(Text, {
    color: "neutral700",
    margin: "0px 0px 20px",
    tag: Heading,
    textAlign: "center",
    type: "sectionHeader1Medium"
  }, "Your Zap is on!"), jsx(Text, {
    color: "neutral700",
    tag: "p",
    textAlign: "center",
    type: "paragraph1"
  }, description)), jsx(ButtonGroup, {
    columnSize: "minmax(220px, 1fr)",
    justify: "center"
  }, jsx(Button, {
    color: "secondary",
    href: goToMyZapsUrl,
    onClick: trackGoToMyZaps
  }, "Go to My Zaps"), jsx(Button, {
    href: transferDataUrl,
    isFullWidth: true,
    onClick: trackTransferExistingData
  }, "Transfer existing data"))))));
};

var PostSetupModal$1 = PostSetupModal;

/** @jsxRuntime classic */

const ArrowConnector = _ref => {
  let {
    color = 'neutral700',
    width = 63
  } = _ref;
  return jsx("svg", {
    fill: "none",
    height: "36",
    viewBox: "0 0 63 36",
    width: width,
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("path", {
    d: "M62.3536 18.3536C62.5488 18.1583 62.5488 17.8417 62.3536 17.6464L59.1716 14.4645C58.9763 14.2692 58.6597 14.2692 58.4645 14.4645C58.2692 14.6597 58.2692 14.9763 58.4645 15.1716L61.2929 18L58.4645 20.8284C58.2692 21.0237 58.2692 21.3403 58.4645 21.5355C58.6597 21.7308 58.9763 21.7308 59.1716 21.5355L62.3536 18.3536ZM25.1139 18L25.1139 18.5L25.6139 18.5L25.6139 18L25.1139 18ZM25.1139 1L25.6092 0.931411L24.6139 1L25.1139 1ZM29.8228 18L29.8228 17.5L29.3228 17.5L29.3228 18L29.8228 18ZM29.8228 35L29.3275 35.0686L30.3228 35L29.8228 35ZM-2.99665e-07 18.5L25.1139 18.5L25.1139 17.5L-3.17827e-07 17.5L-2.99665e-07 18.5ZM32.1772 18.5L62 18.5L62 17.5L32.1772 17.5L32.1772 18.5ZM25.6139 18L25.6139 1L24.6139 1L24.6139 18L25.6139 18ZM29.8228 18.5L32.1772 18.5L32.1772 17.5L29.8228 17.5L29.8228 18.5ZM24.6187 1.0686L29.3275 35.0686L30.3181 34.9314L25.6092 0.931411L24.6187 1.0686ZM30.3228 35L30.3228 18L29.3228 18L29.3228 35L30.3228 35Z",
    fill: Colors[color]
  }));
};

var ArrowConnectorSVG = ArrowConnector;

/** @jsxRuntime classic */

const Thunder = _ref => {
  let {
    color = 'neutral700'
  } = _ref;
  return jsx("svg", {
    fill: "none",
    height: "34",
    viewBox: "0 -1 45 40",
    width: "45",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("path", {
    d: "M0 17C0 7.61116 7.61116 0 17 0H28C37.3888 0 45 7.61116 45 17V17C45 26.3888 37.3888 34 28 34H17C7.61116 34 0 26.3888 0 17V17Z",
    fill: "#FFE7D1"
  }), jsx("path", {
    d: "M21.4586 26.375H20.4169L21.4586 19.0833H17.8128C17.2086 19.0833 17.219 18.75 17.4169 18.3958C17.6149 18.0417 17.469 18.3125 17.4899 18.2708C18.8336 15.8958 20.8544 12.3542 23.542 7.625H24.5836L23.542 14.9167H27.1878C27.6982 14.9167 27.7711 15.2604 27.6774 15.4479L27.6045 15.6042C23.5003 22.7812 21.4586 26.375 21.4586 26.375Z",
    fill: "#FF4F00"
  }), jsx("path", {
    d: "M17 1H28V-1H17V1ZM28 33H17V35H28V33ZM17 33C8.16344 33 1 25.8366 1 17H-1C-1 26.9411 7.05887 35 17 35V33ZM44 17C44 25.8366 36.8366 33 28 33V35C37.9411 35 46 26.9411 46 17H44ZM28 1C36.8366 1 44 8.16344 44 17H46C46 7.05887 37.9411 -1 28 -1V1ZM17 -1C7.05888 -1 -1 7.05887 -1 17H1C1 8.16344 8.16344 1 17 1V-1Z",
    fill: Colors[color]
  }));
};

var ThunderSVG = Thunder;

/** @jsxRuntime classic */
/**
 * Renders a modal box to allow the user
 * to start a new transfer
 *
 * @param GetStartedModalProps
 * @returns GetStartedModalComponent
 */

const GetStartedModal = _ref => {
  let {
    getStartedUrl,
    isOpen,
    readApp,
    writeApp
  } = _ref;
  const [open, setOpen] = useState(isOpen); // Need to do this so that local state is reset if the zap is turned
  // off and back on.

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const closePopover = () => {
    setOpen(false);
  };

  if (!open) {
    return null;
  }

  const responsiveSize = getResponsiveSize();
  const isDesktop = responsiveSize === 'desktop';
  const isMobile = responsiveSize === 'mobile';
  return jsx(Modal, {
    ariaLabel: "Get Started",
    onClosed: closePopover
  }, jsx(ModalContent, {
    maxWidth: isDesktop ? 'min-content' : 'fit-content;'
  }, jsx("div", {
    css: styles$1.containerStyles
  }, jsx("section", {
    css: styles$1.contentStyles
  }, jsx("section", {
    css: styles$1.apps
  }, jsx(ServiceIcon, {
    serviceName: readApp.name,
    size: isDesktop ? 120 : 60,
    src: readApp.src
  }), jsx(ArrowConnectorSVG, {
    width: isMobile ? 54 : 63
  }), jsx(Spacer, {
    width: isDesktop ? 20 : 10
  }), jsx("div", {
    css: styles$1.iconBadgeContainer
  }, jsx(ServiceIcon, {
    serviceName: writeApp.name,
    size: isDesktop ? 120 : 60,
    src: writeApp.src
  }), jsx(ThunderSVG, null))), jsx("div", {
    css: styles$1.textWrapper
  }, jsx(Text, {
    margin: "0px 0px 20px",
    tag: Heading,
    textAlign: "center",
    type: "sectionHeader1Medium"
  }, "Transfer existing data"), jsx(Text, {
    color: "neutral700",
    tag: "p",
    textAlign: "center",
    type: isDesktop ? 'paragraph3' : 'smallPrint1'
  }, "Transfer historical data from ", readApp.name, " to bring your", ' ', writeApp.name, " up-to-date.")), jsx("section", {
    css: styles$1.buttonWrapper
  }, jsx(Button, {
    href: getStartedUrl,
    size: "small"
  }, "Get started"))))));
};

var GetStartedModal$1 = GetStartedModal;

var _templateObject$1, _templateObject2$1, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9;
const COLUMN_GAP = 30;
const COLUMN_MAX_WIDTH = 600;
const MAX_SCALE = 1;
const MIN_SCALE = 0.7;
const root = containerWidth => [css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n    display: grid;\n    grid-gap: 20px ", "px;\n    margin: auto;\n    max-width: 1000px;\n  "])), COLUMN_GAP), containerWidth > COLUMN_MAX_WIDTH ? css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n        grid-template-columns: repeat(3, minmax(0, 1fr));\n        grid-template-rows: repeat(2, auto);\n        grid-template-areas: 'graphic1 graphic2 graphic3' 'caption1 caption2 caption3';\n      "]))) : css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n        grid-template-columns: auto;\n        grid-template-rows: repeat(6, auto);\n        grid-template-areas: 'graphic1' 'caption1' 'graphic2' 'caption2' 'graphic3' 'caption3';\n      "])))];
const graphic = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: flex-end;\n  justify-content: center;\n"])));

const svgCss = (width, height, index, offsetY) => containerWidth => {
  const columnWidth = (containerWidth - COLUMN_GAP * 2) / 3;
  const scale = columnWidth / 323; // scale all SVGs according to the widest SVG

  const scaleConstrained = Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE);
  return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      position: relative;\n      grid-area: ", ";\n      height: ", "px;\n      width: ", "px;\n      top: ", "px;\n    "])), 'graphic' + index, height * scaleConstrained, width * scaleConstrained, offsetY * scaleConstrained);
};

const step1 = svgCss(270, 140, 1, 8);
const step2 = svgCss(175, 140, 2, 9);
const step3 = svgCss(323, 145, 3, 20);
const step3Position = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  top: 10px !important;\n"])));
const caption = index => css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n  text-align: center;\n  grid-area: ", ";\n"])), 'caption' + index);
const heading = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n  margin: 0 0 10px 0;\n  color: ", ";\n  ", "\n"])), Colors.gray80, Typography.pageHeader9);
const description = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n  margin: 0;\n  color: ", ";\n  ", "\n"])), Colors.gray80, Typography.paragraph3);

/** @jsxRuntime classic */

const Steps = _ref => {
  let {
    width
  } = _ref;
  return jsx("section", {
    css: root(width)
  }, jsx("div", {
    css: graphic,
    role: "presentation"
  }, jsx("svg", {
    css: step1(width),
    fill: "none",
    height: "125",
    viewBox: "0 0 270 125",
    width: "270",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("rect", {
    fill: "#FFFDF9",
    height: "106",
    rx: "12.5",
    stroke: "#2D2E2E",
    width: "106",
    x: "9.5",
    y: "9.5"
  }), jsx("rect", {
    fill: "#FF4F00",
    height: "60",
    rx: "5",
    width: "60",
    x: "33",
    y: "32"
  }), jsx("circle", {
    cx: "63",
    cy: "62",
    fill: "#FFFDF9",
    r: "15"
  }), jsx("rect", {
    fill: "#FFFDF9",
    height: "106",
    rx: "12.5",
    stroke: "#2D2E2E",
    width: "106",
    x: "154.5",
    y: "9.5"
  }), jsx("rect", {
    fill: "#C1B7FF",
    height: "60",
    rx: "5",
    width: "60",
    x: "178",
    y: "32"
  }), jsx("path", {
    d: "M207.5 47L222.655 73.25H192.345L207.5 47Z",
    fill: "#FFFDF9"
  }), jsx("path", {
    d: "M115 62L155 62",
    stroke: "#2D2E2E"
  }))), jsx("div", {
    css: caption(1)
  }, jsx("p", {
    css: heading
  }, "1. Connect your apps"), jsx("p", {
    css: description
  }, "Connect your data source and destination apps")), jsx("div", {
    css: graphic,
    role: "presentation"
  }, jsx("svg", {
    css: step2(width),
    fill: "none",
    height: "125",
    viewBox: "0 0 159 125",
    width: "159",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("mask", {
    height: "105",
    id: "mask0_2906_80819",
    maskUnits: "userSpaceOnUse",
    style: {
      maskType: 'alpha'
    },
    width: "139",
    x: "10",
    y: "10"
  }, jsx("rect", {
    fill: "#0D71F3",
    height: "105",
    rx: "8.75",
    width: "138.25",
    x: "10.375",
    y: "10"
  })), jsx("g", {
    mask: "url(#mask0_2906_80819)"
  }, jsx("rect", {
    fill: "#FFF3E6",
    height: "105",
    width: "138.25",
    x: "10.375",
    y: "10"
  }), jsx("path", {
    d: "M163.23 26V25.5H162.73H65.7305C58.8269 25.5 53.2305 31.0964 53.2305 38V116V116.5H53.7305H152.285C158.33 116.5 163.23 111.599 163.23 105.554V26Z",
    fill: "#FFFDF9",
    stroke: "#2D2E2E"
  }), jsx("rect", {
    fill: "#D7D5D2",
    height: "5.51597",
    rx: "2.75799",
    width: "40.4505",
    x: "65.375",
    y: "60.3711"
  }), jsx("rect", {
    fill: "#FF4F00",
    height: "15",
    rx: "1",
    width: "15",
    x: "66.0547",
    y: "37"
  }), jsx("circle", {
    cx: "73.5547",
    cy: "44.5",
    fill: "#FFFDF9",
    r: "3.75"
  }), jsx("rect", {
    fill: "#D7D5D2",
    height: "5.51597",
    rx: "2.75799",
    width: "20.2252",
    x: "113.18",
    y: "60.373"
  }), jsx("rect", {
    fill: "#D7D5D2",
    height: "5.51597",
    rx: "2.75799",
    width: "40.4505",
    x: "65.375",
    y: "71.4043"
  }), jsx("rect", {
    fill: "#D7D5D2",
    height: "5.51597",
    rx: "2.75799",
    width: "20.2252",
    x: "113.18",
    y: "71.4043"
  }), jsx("rect", {
    fill: "#D7D5D2",
    height: "5.51597",
    rx: "2.75799",
    width: "40.4505",
    x: "65.375",
    y: "82.4355"
  }), jsx("rect", {
    fill: "#D7D5D2",
    height: "5.51597",
    rx: "2.75799",
    width: "20.2252",
    x: "113.18",
    y: "82.4355"
  }), jsx("rect", {
    fill: "#3D4592",
    height: "6.80669",
    rx: "1.09307",
    width: "31.3108",
    x: "87.7305",
    y: "95"
  })), jsx("rect", {
    height: "104.125",
    rx: "11.5625",
    stroke: "black",
    "stroke-width": "0.875",
    width: "137.375",
    x: "10.8125",
    y: "10.4375"
  }))), jsx("div", {
    css: caption(2)
  }, jsx("p", {
    css: heading
  }, "2. Select data to move"), jsx("p", {
    css: description
  }, "Filter and select which data to send")), jsx("div", {
    css: graphic,
    role: "presentation"
  }, jsx("svg", {
    css: [step3(width), step3Position],
    fill: "none",
    height: "145",
    viewBox: "0 0 323 145",
    width: "323",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("rect", {
    fill: "#FFFDF9",
    height: "106",
    rx: "12.5",
    stroke: "#2D2E2E",
    width: "106",
    x: "19.5",
    y: "19.5"
  }), jsx("rect", {
    fill: "#FF4F00",
    height: "60",
    rx: "5",
    width: "60",
    x: "43",
    y: "42"
  }), jsx("circle", {
    cx: "73",
    cy: "72",
    fill: "#FFFDF9",
    r: "15"
  }), jsx("rect", {
    fill: "#FFFDF9",
    height: "106",
    rx: "12.5",
    stroke: "#2D2E2E",
    width: "106",
    x: "195.5",
    y: "19.5"
  }), jsx("rect", {
    fill: "#C1B7FF",
    height: "60",
    rx: "5",
    width: "60",
    x: "219",
    y: "42"
  }), jsx("path", {
    d: "M248.5 57L263.655 83.25H233.345L248.5 57Z",
    fill: "#FFFDF9"
  }), jsx("path", {
    d: "M177.665 72.3536C177.861 72.1583 177.861 71.8417 177.665 71.6464L174.483 68.4645C174.288 68.2692 173.971 68.2692 173.776 68.4645C173.581 68.6597 173.581 68.9763 173.776 69.1716L176.605 72L173.776 74.8284C173.581 75.0237 173.581 75.3403 173.776 75.5355C173.971 75.7308 174.288 75.7308 174.483 75.5355L177.665 72.3536ZM125 72.5L177.312 72.5L177.312 71.5L125 71.5L125 72.5Z",
    fill: "#2D2E2E"
  }), jsx("rect", {
    fill: "#FFF3E6",
    height: "33",
    rx: "16.5",
    width: "44",
    x: "278.5",
    y: "1.5"
  }), jsx("g", {
    "clip-path": "url(#clip0_2921_80417)"
  }, jsx("path", {
    d: "M297.376 30.1452L309.397 15.8223H303.626V5.66602L291.605 19.9889H297.376V30.1452Z",
    fill: "#FF4F00"
  })), jsx("rect", {
    height: "33",
    rx: "16.5",
    stroke: "#2D2E2E",
    width: "44",
    x: "278.5",
    y: "1.5"
  }), jsx("defs", null, jsx("clipPath", {
    id: "clip0_2921_80417"
  }, jsx("rect", {
    fill: "white",
    height: "25",
    transform: "translate(288 5.5)",
    width: "25"
  }))))), jsx("div", {
    css: caption(3)
  }, jsx("p", {
    css: heading
  }, "3. Transfer your data"), jsx("p", {
    css: description
  }, "Send your data from one app to another")));
};

var Steps$1 = Steps;

/** @jsxRuntime classic */

const buildGivesByKey = gives => gives.reduce((lookup, give) => {
  lookup[give.key] = give;
  return lookup;
}, {});

const giveName = (givesByKey, key) => givesByKey[key] ? givesByKey[key].label : key;

const filterName = _ref => {
  let {
    match,
    action
  } = _ref;
  return CONDITIONS_BY_VALUE["".concat(match, "|").concat(action)].description;
};
/**
 * This component renders the unstyled HTML for describing groups of filters.
 * To style this component and all the elements it contains,
 * include a `css` prop which will be applied to the root `<ul>` tag.
 */


const FilterDescriptions = _ref2 => {
  let {
    gives,
    filters,
    className,
    groupSeparator
  } = _ref2;
  const criterionGroups = useMemo(() => groupFilters(filters), [filters]);
  const givesByKey = useMemo(() => buildGivesByKey(gives), [gives]);

  if (filters.length < 1) {
    return null;
  }

  const groups = criterionGroups.map((group, i) => jsx("li", {
    key: group.group
  }, i !== 0 && groupSeparator, jsx("ul", null, group.criterion.map(crit => jsx("li", {
    key: crit.id
  }, jsx("strong", null, giveName(givesByKey, crit.key)), " ", filterName(crit), ' ', jsx("strong", null, crit.value))))));
  return jsx("ul", {
    className: className
  }, groups);
};

var FilterDescriptions$1 = FilterDescriptions;

var _templateObject, _templateObject2;
const container = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  align-items: center;\n  display: grid;\n  grid-gap: 10px;\n  grid-template-columns: max-content 1fr;\n"])));
css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n"])));

/** @jsxRuntime classic */

/**
 * Renders an Icon and the provided children
 *
 * @param IconCardProps
 * @returns FC
 */
const IconCard = _ref => {
  let {
    iconName,
    iconSize = 24,
    children,
    iconColor = 'neutral700',
    isBlock
  } = _ref;
  return jsx("div", {
    css: container
  }, jsx(Icon, {
    color: iconColor,
    isBlock: isBlock,
    name: iconName,
    size: iconSize
  }), children);
};

var IconCard$1 = IconCard;

let Filter;

(function (_Filter) {})(Filter || (Filter = {}));

let Schedule;

(function (_Schedule) {})(Schedule || (Schedule = {}));

export { CONDITIONS, CONDITIONS_BY_VALUE, DAYS_OF_MONTH, DAYS_OF_WEEK, Filter, FilterDescriptions$1 as FilterDescriptions, GetStartedModal$1 as GetStartedModal, HOURS_OF_DAY, IconCard$1 as IconCard, PostSetupModal$1 as PostSetupModal, Schedule, Steps$1 as Steps, TransferDataOption$1 as TransferDataOption, describeSchedule, domToIndex, dowToIndex, getResponsiveSize, getResponsiveStyles, groupFilters, hodToIndex, indexToDom, indexToDow, indexToHod, mediaQueries };
